// Assembles the USB logcat tap command the ConfigPage / Connect Device card copies.
// Per-user traffic isolation (Open question 1, AC 121-122):
//   - kind 'enrollment': POST /proxy/enroll {platform:'logcat'} returned { id, secret };
//     the command carries `--enrollment id:secret` and NOTHING else secret. Rows are owned.
//   - kind 'jwt': the signed-in user's short-lived proxy JWT as --token (rows owned by the
//     JWT sub, but it expires in about an hour, so it is the fallback, not the default).
//   - kind 'static': REACT_APP_PROXY_API_TOKEN. Rows are UNOWNED (machine-only): the card
//     must show STATIC_TOKEN_TAP_COPY.
//   - kind 'none': no credential at all; a placeholder is copied.
// Never throws.

import { enrollmentCredential } from './isolationCopy';

export const TOKEN_PLACEHOLDER = '<API_TOKEN>';

export function buildTapCommand({ api, deviceName, credential }) {
  const base = `cd proxy-server && npm run tap -- --api ${api}`;
  const name = `--device-name ${deviceName}`;
  const c = credential || { kind: 'none' };
  if (c.kind === 'enrollment') return `${base} --enrollment ${c.value} ${name}`;
  return `${base} --token ${c.value || TOKEN_PLACEHOLDER} ${name}`;
}

/**
 * Resolve the credential to embed. Injected IO so tests never touch the network:
 *   mintEnrollment(): Promise<{id,secret}|null>   (POST /proxy/enroll, platform logcat)
 *   getJwt(): string                               (cached proxy JWT)
 *   refreshJwt(): Promise<{token}|null>
 *   staticToken: string
 */
export async function resolveTapCredential({ mintEnrollment, getJwt, refreshJwt, staticToken }) {
  try {
    const made = mintEnrollment ? await mintEnrollment() : null;
    const cred = enrollmentCredential(made);
    if (cred) return { kind: 'enrollment', value: cred };
  } catch { /* fall through to the token fallbacks */ }
  let jwt = '';
  try { jwt = (getJwt && getJwt()) || ''; } catch { jwt = ''; }
  if (!jwt && refreshJwt) {
    try { const fresh = await refreshJwt(); jwt = (fresh && fresh.token) || ''; } catch { jwt = ''; }
  }
  if (jwt) return { kind: 'jwt', value: jwt };
  if (staticToken) return { kind: 'static', value: staticToken };
  return { kind: 'none', value: '' };
}

export const TAP_ENROLLMENT_KEY = 'fx_tap_enrollment';

/**
 * Reuse the caller's live logcat enrollment instead of minting one per click. The proxy
 * returns the secret ONCE (only its hash is stored; GET /proxy/enrollments never includes
 * it), so the secret is kept in `storage` (sessionStorage: this tab only, gone on close)
 * next to the id, and reused only while GET /proxy/enrollments (viewer-scoped) still lists
 * that id as live, not revoked. Anything else mints a new one. Never throws.
 *   storage: { getItem, setItem, removeItem } | null
 *   listEnrollments(): Promise<Array|null>   (null = could not ask: mint rather than trust a stale secret)
 *   mint(): Promise<{id,secret}|null>
 */
export async function reuseOrMintEnrollment({ storage, listEnrollments, mint, deviceLabel }) {
  const label = String(deviceLabel || '');
  let saved = null;
  try {
    const raw = storage && storage.getItem(TAP_ENROLLMENT_KEY);
    saved = raw ? JSON.parse(raw) : null;
  } catch { saved = null; }
  if (saved && typeof saved.id === 'string' && typeof saved.secret === 'string' && saved.id && saved.secret && saved.label === label) {
    let list = null;
    try { list = listEnrollments ? await listEnrollments() : null; } catch { list = null; }
    const rec = Array.isArray(list) ? list.find((e) => e && e.id === saved.id) : null;
    if (rec && rec.live !== false && !rec.revokedAt) return { id: saved.id, secret: saved.secret };
    try { storage.removeItem(TAP_ENROLLMENT_KEY); } catch { /* ignore */ }
  }
  let made = null;
  try { made = mint ? await mint() : null; } catch { made = null; }
  if (made && enrollmentCredential(made)) {
    try { storage && storage.setItem(TAP_ENROLLMENT_KEY, JSON.stringify({ id: made.id, secret: made.secret, label })); } catch { /* storage full/blocked: just no reuse */ }
  }
  return made;
}

/** True when rows from this command will be unowned (machine-only) once isolation is on. */
export function tapIsUnowned(credential) {
  return !credential || credential.kind === 'static' || credential.kind === 'none';
}
